import { Transaction } from '@mysten/sui/transactions';
import { dgcToMist, mistToDgc } from './amount.js';
import { getCpcBalance } from './balance.js';
import { CPC_CLOB_GAS_BUDGET_MIST } from './buildClob.js';
import { getCoinBalanceView } from './buildTransferCoin.js';
import { CPC_FRAMEWORK, DEFAULT_DATA_TYPE } from './constants.js';
/** Shared Sui Clock object on DGC devnet. */
export const SUI_CLOCK_OBJECT_ID = '0x6';
export const BRIDGE_CHAIN_ID_BNB = 56n;
export const BRIDGE_CHAIN_ID_BNB_TESTNET = 97n;
export const EVM_RECIPIENT_LEN = 20;
export function parseEvmAddressToBytes(addr) {
    let s = addr.trim();
    if (s.startsWith('0x') || s.startsWith('0X'))
        s = s.slice(2);
    if (!/^[0-9a-fA-F]{40}$/.test(s)) {
        throw new Error('Invalid BSC address (expected 0x + 40 hex chars)');
    }
    const bytes = new Uint8Array(EVM_RECIPIENT_LEN);
    for (let i = 0; i < EVM_RECIPIENT_LEN; i++) {
        bytes[i] = Number.parseInt(s.slice(i * 2, i * 2 + 2), 16);
    }
    return bytes;
}
export function formatEvmAddressFromBytes(bytes) {
    if (bytes.length !== EVM_RECIPIENT_LEN) {
        throw new Error(`EVM address must be ${EVM_RECIPIENT_LEN} bytes`);
    }
    return `0x${Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')}`;
}
function resolveDataTypeTag(tag) {
    const s = (tag ?? '').trim();
    return s.length > 0 ? s : DEFAULT_DATA_TYPE;
}
function applyBridgeAddressBalanceGas(tx) {
    tx.setGasPayment([]);
    tx.setGasBudget(CPC_CLOB_GAS_BUDGET_MIST);
}
function appendLockMoveCall(tx, packageId, bridgeId, moveFn, lockCoin, evmRecipientBytes, targetChainId) {
    const recipientArg = tx.pure.vector('u8', Array.from(evmRecipientBytes));
    if (moveFn === 'lock_for_bnb') {
        tx.moveCall({
            target: `${packageId}::bridge::lock_for_bnb`,
            arguments: [tx.object(bridgeId), lockCoin, recipientArg, tx.object(SUI_CLOCK_OBJECT_ID)],
        });
        return;
    }
    tx.moveCall({
        target: `${packageId}::bridge::lock_for_outbound`,
        arguments: [
            tx.object(bridgeId),
            lockCoin,
            tx.pure.u64(targetChainId ?? BRIDGE_CHAIN_ID_BNB_TESTNET),
            recipientArg,
            tx.object(SUI_CLOCK_OBJECT_ID),
        ],
    });
}
function appendLockFromAddressBalance(tx, packageId, bridgeId, moveFn, evmRecipientBytes, amountMist, dataTypeTag, targetChainId) {
    const withdrawal = tx.withdrawal({ amount: amountMist, type: dataTypeTag });
    const balance = tx.moveCall({
        target: `${CPC_FRAMEWORK}::balance::redeem_funds`,
        typeArguments: [dataTypeTag],
        arguments: [withdrawal],
    });
    const lockCoin = tx.moveCall({
        target: `${CPC_FRAMEWORK}::coin::from_balance`,
        typeArguments: [dataTypeTag],
        arguments: [balance],
    });
    appendLockMoveCall(tx, packageId, bridgeId, moveFn, lockCoin, evmRecipientBytes, targetChainId);
}
/**
 * Split exact lock amount from loose `Coin<DATA>` (join if needed). Do not `send_funds`:
 * on-chain precheck does not credit the same-PTB deposit. Gas stays on DGC address balance.
 */
function appendLockFromLooseCoins(tx, packageId, bridgeId, moveFn, evmRecipientBytes, amountMist, coins, dataTypeTag, targetChainId) {
    let source = tx.object(coins[0].coinObjectId);
    const single = coins.find((c) => BigInt(c.balance) >= amountMist);
    if (single) {
        source = tx.object(single.coinObjectId);
    }
    else if (coins.length > 1) {
        for (let i = 1; i < coins.length; i++) {
            tx.moveCall({
                target: `${CPC_FRAMEWORK}::coin::join`,
                typeArguments: [dataTypeTag],
                arguments: [source, tx.object(coins[i].coinObjectId)],
            });
        }
    }
    const [lockCoin] = tx.splitCoins(source, [tx.pure.u64(amountMist)]);
    appendLockMoveCall(tx, packageId, bridgeId, moveFn, lockCoin, evmRecipientBytes, targetChainId);
}
function buildLockTx(params) {
    if (params.evmRecipientBytes.length !== EVM_RECIPIENT_LEN) {
        throw new Error(`BSC recipient must be ${EVM_RECIPIENT_LEN} bytes`);
    }
    const moveFn = params.useTestnet ? 'lock_for_outbound' : 'lock_for_bnb';
    const chainId = params.useTestnet ? BRIDGE_CHAIN_ID_BNB_TESTNET : undefined;
    const tx = new Transaction();
    if (params.from === 'loose-coins') {
        const coins = params.looseCoins ?? [];
        if (coins.length === 0) {
            throw new Error('Bridge lock Coin path: no loose DATA coins');
        }
        appendLockFromLooseCoins(tx, params.packageId, params.bridgeId, moveFn, params.evmRecipientBytes, params.amountMist, coins, params.dataTypeTag, chainId);
        applyBridgeAddressBalanceGas(tx);
        return tx;
    }
    appendLockFromAddressBalance(tx, params.packageId, params.bridgeId, moveFn, params.evmRecipientBytes, params.amountMist, params.dataTypeTag, chainId);
    applyBridgeAddressBalanceGas(tx);
    return tx;
}
/** DATA → BSC mainnet (chain_id=56) from DATA address balance. Gas is DGC. */
export function buildLockForBnbFromAddressBalance({ packageId, bridgeId, amountMist, evmRecipientBytes, dataTypeTag, looseCoinObjectIds = [], }) {
    if (looseCoinObjectIds.length > 0) {
        console.warn('[cpc-tx] buildLockForBnbFromAddressBalance: looseCoinObjectIds ignored (same-PTB send_funds + withdrawal fails precheck). Use buildLockForBnbForOwner.');
    }
    return buildLockTx({
        packageId,
        bridgeId,
        amountMist: BigInt(amountMist),
        evmRecipientBytes,
        useTestnet: false,
        dataTypeTag: resolveDataTypeTag(dataTypeTag),
        from: 'address-balance',
    });
}
/** DATA → BSC Chapel testnet (chain_id=97) from DATA address balance. */
export function buildLockForBnbTestnetFromAddressBalance(params) {
    if (params.looseCoinObjectIds?.length) {
        console.warn('[cpc-tx] buildLockForBnbTestnetFromAddressBalance: looseCoinObjectIds ignored. Use buildLockForBnbForOwner.');
    }
    return buildLockTx({
        packageId: params.packageId,
        bridgeId: params.bridgeId,
        amountMist: BigInt(params.amountMist),
        evmRecipientBytes: params.evmRecipientBytes,
        useTestnet: true,
        dataTypeTag: resolveDataTypeTag(params.dataTypeTag),
        from: 'address-balance',
    });
}
async function fetchLooseCoinsWithBalance(client, owner, coinType) {
    const coins = [];
    let cursor = undefined;
    for (;;) {
        const resp = await client.getCoins({ owner, coinType, cursor });
        for (const c of resp.data ?? []) {
            if (BigInt(c.balance) > 0n) {
                coins.push({
                    coinObjectId: c.coinObjectId,
                    balance: c.balance,
                    version: c.version,
                    digest: c.digest,
                });
            }
        }
        if (!resp.hasNextPage || !resp.nextCursor)
            break;
        cursor = resp.nextCursor;
    }
    return coins;
}
/**
 * Build DATA → BNB USDT lock.
 *
 * On-chain `lock_for_bnb` takes `Coin<DATA>`. Gas is always DGC (address balance).
 * Never `send_funds` in the same PTB as `redeem_funds`.
 */
export async function buildLockForBnbForOwner(client, owner, params) {
    const amountMist = dgcToMist(String(params.amountCpc));
    const gasReserve = CPC_CLOB_GAS_BUDGET_MIST;
    const dataTypeTag = resolveDataTypeTag(params.dataTypeTag);
    const dataBal = await getCoinBalanceView(client, owner, dataTypeTag);
    if (dataBal.totalMist < amountMist) {
        throw new Error(`DATA 不足：锁仓 ${mistToDgc(amountMist)} DATA，持有约 ${mistToDgc(dataBal.totalMist)} DATA`);
    }
    const dgcBal = await getCpcBalance(client, owner);
    if (dgcBal.addressBalanceMist < gasReserve && dgcBal.totalMist < gasReserve) {
        throw new Error(`DGC 不够付 Gas：跨链锁仓需约 ${mistToDgc(gasReserve)} DGC（主币），当前约 ${mistToDgc(dgcBal.totalMist)} DGC。`);
    }
    const looseCoins = await fetchLooseCoinsWithBalance(client, owner, dataTypeTag);
    const looseMist = looseCoins.reduce((s, c) => s + BigInt(c.balance), 0n);
    const base = {
        packageId: params.packageId,
        bridgeId: params.bridgeId,
        amountMist,
        evmRecipientBytes: params.evmRecipientBytes,
        useTestnet: Boolean(params.useTestnet),
        dataTypeTag,
    };
    if (dataBal.addressBalanceMist >= amountMist) {
        return buildLockTx({ ...base, from: 'address-balance' });
    }
    if (looseMist >= amountMist) {
        return buildLockTx({
            ...base,
            from: 'loose-coins',
            looseCoins,
        });
    }
    throw new Error(`DATA 不足：锁仓 ${mistToDgc(amountMist)} DATA。` +
        `地址余额 ${mistToDgc(dataBal.addressBalanceMist)}，零散 Coin ${mistToDgc(looseMist)}。` +
        `不要在锁仓同一笔里 send_funds 归集。`);
}
